import { Component, OnInit, ElementRef, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ViewTableComponent } from 'src/app/components/base/view-table/view-table.component';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { PackagesService } from 'src/app/services/packages.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { PrintService } from 'src/app/services/print.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { WeekYearChangeEvent } from '../../shared/week-year-selector/week-year-selector.component';
import { CurrencyService } from '../../../services/currency.service';

type BucketKey = 'PRE' | 'ORI' | 'EXT' | 'REN' | 'MISC' | 'SUNDRY' | 'NONUNIT' | 'REFUND';
type Buckets = Record<BucketKey, number>;

interface CashReportResponse {
  ytd: Buckets;
  week: Buckets;
  instructors: { uid: number; name: string; ytd: Buckets; week: Buckets }[];
  active_buckets: BucketKey[];
}

const ALL_BUCKETS: BucketKey[] = ['PRE', 'ORI', 'EXT', 'REN', 'MISC', 'SUNDRY', 'NONUNIT', 'REFUND'];

const EMPTY_BUCKETS: Buckets = {
  PRE: 0, ORI: 0, EXT: 0, REN: 0, MISC: 0, SUNDRY: 0, NONUNIT: 0, REFUND: 0,
};

// Observed status values on real payment data (the field storage config has a
// slightly inconsistent list). These are the labels the UI offers.
const STATUS_OPTIONS: string[] = ['Paid', 'Refund', 'Refunded', 'Canceled', 'Unpaid', 'Failed', 'Pending'];

@Component({
  selector: 'app-cash-report',
  templateUrl: './cash-report.component.html',
  styleUrls: ['./cash-report.component.css']
})
export class CashReportComponent extends ViewTableComponent implements OnInit {

  constructor(
    override _refreshService: RefreshService,
    override _activatedRoute: ActivatedRoute,
    override _fieldsService: FieldsService,
    override _drupalRESTService: DrupalRESTService,
    override _authService: AuthService,
    override _dialogService: DialogService,
    override _entityRESTService: EntityRESTService,
    override _utilityService: UtilityService,
    override _taxonomyService: TaxonomyService,
    override _router: Router,
    override _authRESTService: AuthRESTService,
    override _menuRESTService: MenuRESTService,
    override _formBuilder: FormBuilder,
    override _routeEventsService: RouteEventsService,
    override _permissionsService: PermissionsService,
    override _renderer2: Renderer2,
    override _configPages: ConfigPagesService,
    override _packagesService: PackagesService,
    override _snackBar: MatSnackBar,
    override _userRESTService: UserService,
    override _el: ElementRef,
    override _changeDetectorRef: ChangeDetectorRef,
    override _sanitizer: DomSanitizer,
    override _studioConfigService: StudioConfigService,
    override _componentRefreshService: ComponentRefreshService,
    override _translateService: TranslateService,
    private printService: PrintService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService,
    );
  }

  storage: any = {};
  JSObject: Object = Object;
  environment = environment;

  override displayedColumns: string[] = ['nothing'];

  CashReportData: Buckets = { ...EMPTY_BUCKETS };
  CashReportDataWEEK: Buckets = { ...EMPTY_BUCKETS };

  week;
  year;

  // Filters
  includeInactive = false;
  includeHistory = true;
  filterInstructorUid: number | null = null;
  filterPaymentTypes: number[] = [];
  filterDepartments: number[] = [];
  filterBuckets: BucketKey[] = [];          // empty = all
  filterCategories: number[] = [];
  filterStatuses: string[] = [];            // empty = Royalty defaults

  // Dropdown option sources (exposed to the template)
  readonly allBuckets = ALL_BUCKETS;
  readonly statusOptions = STATUS_OPTIONS;
  activeBuckets: BucketKey[] = ALL_BUCKETS;  // reflects server response

  onWeekYearChange(event: WeekYearChangeEvent): void {
    this.week = event.week;
    this.year = event.year;
    this.reload();
  }

  onFilterChange(): void {
    this.reload();
  }

  private reload(): void {
    this.getData([
      { parameter: 'week', value: this.week },
      { parameter: 'year', value: this.year },
    ]);
  }

  override ngAfterContentInit(): void {
    this._activatedRoute.queryParams.subscribe(data => {
      this.ShowProgressBar = false;

      this.year = data['year'] ?? this._fieldsService.getCorrectYearForWeek(new Date()).toString();
      this.week = data['week'] ?? this._fieldsService.getCurrentWeek().toString();

      this.WeeksForYearData = this._fieldsService.WeeksForYearData(parseInt(this.year, 10));

      this.reload();
    });
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    const selectedWeek = +this.week;
    const selectedYear = +this.year;

    // The week/YTD date ranges are passed as half-open intervals [start, end):
    // the backend uses strict < on the end bound, so we add one day to each
    // inclusive end date to capture all datetimes on the final day.
    const addOneDay = (dateStr: string): string =>
      moment(dateStr, 'YYYY-MM-DD', true).add(1, 'day').format('YYYY-MM-DD');

    const weekRange = this._fieldsService.getWeekDateRange(selectedYear, selectedWeek);
    const week1Range = this._fieldsService.getWeekDateRange(selectedYear, 1);

    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.ShowProgressBar = false;
      return;
    }

    const queryParts = [
      `ytd_start=${week1Range.startDate}`,
      `ytd_end=${addOneDay(weekRange.endDate)}`,
      `week_start=${weekRange.startDate}`,
      `week_end=${addOneDay(weekRange.endDate)}`,
      `studio=${studioId}`,
    ];
    if (this.includeInactive) queryParts.push('include_inactive=1');
    if (this.includeHistory) queryParts.push('include_history=1');
    if (this.filterInstructorUid) queryParts.push(`instructor_uid=${this.filterInstructorUid}`);
    if (this.filterPaymentTypes.length) queryParts.push(`payment_types=${this.filterPaymentTypes.join(',')}`);
    if (this.filterDepartments.length) queryParts.push(`departments=${this.filterDepartments.join(',')}`);
    if (this.filterBuckets.length) queryParts.push(`buckets=${this.filterBuckets.join(',')}`);
    if (this.filterCategories.length) queryParts.push(`categories=${this.filterCategories.join(',')}`);
    if (this.filterStatuses.length) queryParts.push(`statuses=${this.filterStatuses.map(encodeURIComponent).join(',')}`);

    const queryString = queryParts.join('&');

    this.dataSource = new MatTableDataSource([0]);
    this.storage = {};

    this._drupalRESTService.httpGET(`/api/cash-report/summary?${queryString}`, params)
      .subscribe(
        (data: CashReportResponse) => {
          this.activeBuckets = data?.active_buckets ?? ALL_BUCKETS;
          this.CashReportData = { ...EMPTY_BUCKETS, ...(data?.ytd ?? {}) };
          this.CashReportDataWEEK = { ...EMPTY_BUCKETS, ...(data?.week ?? {}) };

          const storage: Record<string, any> = {};
          for (const row of data?.instructors ?? []) {
            storage[row.uid] = this.flattenInstructor(row);
          }
          this.storage = storage;
          this.ShowProgressBar = false;
        },
        error => {
          this.ShowProgressBar = false;
          this.handleError(error);
        },
      );
  }

  override onPrint(): void {
    // The app-global print stylesheet hides everything except .receipt-content,
    // so window.print() directly produces a blank page. Build the report HTML
    // and hand it to PrintService, which opens it in a fresh window.
    const weekLabel = `Week ${this.week}, ${this.year}`;
    const buckets = this.activeBuckets;

    const headerRow = ['', ...buckets.map(b => this.bucketLabel(b)), 'TOTAL']
      .map(h => `<th>${h}</th>`).join('');

    // REFUND is subtracted (money went back out); every other bucket is added.
    const sumBuckets = (b: Buckets): number =>
      buckets.reduce((acc, key) => acc + (key === 'REFUND' ? -(b[key] ?? 0) : (b[key] ?? 0)), 0);

    const moneyRow = (label: string, b: Buckets) =>
      `<tr><td><strong>${label}</strong></td>${buckets.map(k => `<td>${this.formatMoney(b[k])}</td>`).join('')}<td>${this.formatMoney(sumBuckets(b))}</td></tr>`;

    const summaryTable = `
      <table class="report-table">
        <thead><tr>${headerRow}</tr></thead>
        <tbody>
          ${moneyRow('Week', this.CashReportDataWEEK)}
          ${moneyRow('YTD', this.CashReportData)}
        </tbody>
      </table>`;

    const instructorSections = this.convertToArray(this.storage)
      .map((row: any) => {
        const ytd = this.pickBuckets(row, false);
        const week = this.pickBuckets(row, true);
        return `
          <div class="instructor-section">
            <h3 class="instructor-name">${this.escapeHtml(row?.name ?? '')}</h3>
            <table class="report-table">
              <thead><tr>${headerRow}</tr></thead>
              <tbody>
                ${moneyRow('Week', week)}
                ${moneyRow('YTD', ytd)}
              </tbody>
            </table>
          </div>`;
      })
      .join('');

    const content = `
      <div class="report-container">
        <h1 class="report-title">Cash Report</h1>
        <h2 class="report-subtitle">${weekLabel}</h2>
        ${summaryTable}
        ${instructorSections}
      </div>`;

    this.printService.print(content, {
      title: `Cash Report - ${weekLabel}`,
      styles: `
        @media print {
          .instructor-section { page-break-inside: avoid; }
        }
      `,
      showSchoolName: true,
      promptPrint: true,
    });
  }

  private bucketLabel(b: BucketKey): string {
    if (b === 'NONUNIT') return 'NON UNIT';
    if (b === 'REFUND') return 'REFUNDS';
    return b;
  }

  private formatMoney(amount: number | undefined): string {
    return this.currencyService.format(amount ?? 0);
  }

  // Instructor cards store buckets as flat keys ("PRE"/"WEEK_PRE"). Rehydrate.
  private pickBuckets(row: any, week: boolean): Buckets {
    const out: Buckets = { ...EMPTY_BUCKETS };
    for (const k of ALL_BUCKETS) {
      const src = week ? `WEEK_${k}` : k;
      out[k] = row?.[src] ?? 0;
    }
    return out;
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  override resetForm(): void {
    this.includeInactive = false;
    this.includeHistory = true;
    this.filterInstructorUid = null;
    this.filterPaymentTypes = [];
    this.filterDepartments = [];
    this.filterBuckets = [];
    this.filterCategories = [];
    this.filterStatuses = [];
    this.reload();
  }

  // The cash-report-individual card template reads flat keys (PRE, ORI, …,
  // WEEK_PRE, WEEK_ORI, …). Flatten the nested {ytd, week} payload to match.
  private flattenInstructor(row: { uid: number; name: string; ytd: Buckets; week: Buckets }) {
    const ytd = { ...EMPTY_BUCKETS, ...row.ytd };
    const week = { ...EMPTY_BUCKETS, ...row.week };
    return {
      name: row.name,
      ...ytd,
      WEEK_PRE: week.PRE,
      WEEK_ORI: week.ORI,
      WEEK_EXT: week.EXT,
      WEEK_REN: week.REN,
      WEEK_MISC: week.MISC,
      WEEK_SUNDRY: week.SUNDRY,
      WEEK_NONUNIT: week.NONUNIT,
      WEEK_REFUND: week.REFUND,
    };
  }

}
